import { BaseClient, type BaseClientOptions } from './http/BaseClient.js';
import { AuthClient } from './modules/auth/AuthClient.js';
import { MfaClient } from './modules/mfa/MfaClient.js';
import { UserClient } from './modules/user/UserClient.js';
import { OrganizationClient } from './modules/organization/OrganizationClient.js';
import { ProjectsClient } from './modules/projects/ProjectsClient.js';
import { SandboxesClient } from './modules/sandboxes/SandboxesClient.js';
import { CredentialsClient } from './modules/credentials/CredentialsClient.js';
import { ChatClient } from './modules/chat/ChatClient.js';
import { TemplatesClient } from './modules/agentEngine/TemplatesClient.js';
import { ScheduledTasksClient } from './modules/agentEngine/ScheduledTasksClient.js';
import { ResourcesClient } from './modules/resources/ResourcesClient.js';
import { SystemEventsClient } from './modules/systemEvents/SystemEventsClient.js';
import { SubscriptionsClient } from './modules/billing/SubscriptionsClient.js';
import { CreditsClient } from './modules/billing/CreditsClient.js';
import { UsageClient } from './modules/usage/UsageClient.js';
import { BudgetsClient } from './modules/budgets/BudgetsClient.js';
import { ReferralsClient } from './modules/referrals/ReferralsClient.js';
import { ResellerClient } from './modules/reseller/ResellerClient.js';
import { PlatformAdminClient } from './modules/platformAdmin/PlatformAdminClient.js';
import { DeploymentsClient } from './modules/deployments/DeploymentsClient.js';
import { BIClient } from './modules/bi/BIClient.js';

export type CloudAgentClientOptions = BaseClientOptions;

class AgentEngineFacade {
  constructor(
    public readonly templates: TemplatesClient,
    public readonly scheduledTasks: ScheduledTasksClient,
  ) {}
}

class BillingFacade {
  constructor(
    public readonly subscriptions: SubscriptionsClient,
    public readonly credits: CreditsClient,
  ) {}
}

export class CloudAgentClient {
  readonly http: BaseClient;
  readonly auth: AuthClient;
  readonly mfa: MfaClient;
  readonly user: UserClient;
  readonly organization: OrganizationClient;
  readonly projects: ProjectsClient;
  readonly sandboxes: SandboxesClient;
  readonly credentials: CredentialsClient;
  readonly chat: ChatClient;
  readonly agentEngine: AgentEngineFacade;
  readonly resources: ResourcesClient;
  readonly systemEvents: SystemEventsClient;
  readonly billing: BillingFacade;
  readonly usage: UsageClient;
  readonly budgets: BudgetsClient;
  readonly referrals: ReferralsClient;
  readonly reseller: ResellerClient;
  readonly platformAdmin: PlatformAdminClient;
  readonly deployments: DeploymentsClient;
  readonly bi: BIClient;

  constructor(options: CloudAgentClientOptions) {
    this.http = new BaseClient(options);
    this.auth = new AuthClient(this.http);
    this.mfa = new MfaClient(this.http);
    this.user = new UserClient(this.http);
    this.organization = new OrganizationClient(this.http);
    this.projects = new ProjectsClient(this.http);
    this.sandboxes = new SandboxesClient(this.http);
    this.credentials = new CredentialsClient(this.http);
    this.chat = new ChatClient(this.http, { baseUrl: this.http.baseUrl, getToken: options.getToken });
    this.agentEngine = new AgentEngineFacade(
      new TemplatesClient(this.http),
      new ScheduledTasksClient(this.http),
    );
    this.resources = new ResourcesClient(this.http);
    this.systemEvents = new SystemEventsClient(this.http);
    this.billing = new BillingFacade(new SubscriptionsClient(this.http), new CreditsClient(this.http));
    this.usage = new UsageClient(this.http);
    this.budgets = new BudgetsClient(this.http);
    this.referrals = new ReferralsClient(this.http);
    this.reseller = new ResellerClient(this.http);
    this.platformAdmin = new PlatformAdminClient(this.http);
    this.deployments = new DeploymentsClient(this.http);
    this.bi = new BIClient(this.http, this.http.baseUrl);
  }
}
